import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './download-pdf-button.css';

const EPS = 0.05; // mm tolerance for boundary float comparisons

// Only "leaf" paragraphs/list items count as unsplittable text blocks — a <p> or <li>
// that wraps other block content (images, tables, nested lists) is a layout container,
// not a single piece of text, so it's excluded and its real children are used instead.
const isLeafParagraph = (el) => !el.querySelector('p, table, img, ul, ol');
const isLeafListItem = (el) => !el.querySelector('ul, ol');

const getAtomicBlockElements = (root) => {
    const blocks = [];
    root.querySelectorAll('p').forEach((el) => {
        if (isLeafParagraph(el)) blocks.push(el);
    });
    root.querySelectorAll('img, table').forEach((el) => blocks.push(el));
    root.querySelectorAll('li').forEach((el) => {
        if (isLeafListItem(el)) blocks.push(el);
    });
    return blocks;
};

const toMmRange = (el, containerTop, mmPerCssPx) => {
    const rect = el.getBoundingClientRect();
    return {
        top: (rect.top - containerTop) * mmPerCssPx,
        bottom: (rect.bottom - containerTop) * mmPerCssPx,
    };
};

// Finds the vertical position (in mm, in the tall stitched image) where each PDF page
// should end, nudging a naive fixed-height cut earlier so it never lands inside a block.
// Falls back to breaking at table rows when a single block is itself taller than a page.
const computePageBreaks = (totalHeightMm, pageHeightMm, blocks, rowBlocks) => {
    const breaks = [];
    let cursor = 0;

    while (totalHeightMm - cursor > EPS) {
        const naiveEnd = cursor + pageHeightMm;
        if (naiveEnd >= totalHeightMm - EPS) {
            breaks.push(totalHeightMm);
            break;
        }

        const straddler = blocks.find(
            (b) => b.top < naiveEnd - EPS && b.bottom > naiveEnd + EPS && b.top >= cursor - EPS
        );

        let end = naiveEnd;
        if (straddler) {
            if (straddler.top > cursor + EPS) {
                end = straddler.top;
            } else {
                const innerStraddler = rowBlocks.find(
                    (b) =>
                        b.top < naiveEnd &&
                        b.bottom > naiveEnd &&
                        b.top > cursor + EPS &&
                        b.top >= straddler.top &&
                        b.bottom <= straddler.bottom
                );
                end = innerStraddler ? innerStraddler.top : naiveEnd;
            }
        }

        if (end <= cursor + EPS) end = naiveEnd; // guard against zero-progress loops
        breaks.push(end);
        cursor = end;
    }

    return breaks;
};

const DownloadPdfButton = ({ contentRef, filename = 'document' }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadPdf = async () => {
        const element = contentRef.current;
        if (!element) return;

        setIsDownloading(true);
        try {
            const reference = new jsPDF('p', 'mm', 'a4');
            const pageWidth = reference.internal.pageSize.getWidth();
            const pageHeight = reference.internal.pageSize.getHeight();

            const containerRect = element.getBoundingClientRect();
            const mmPerCssPx = pageWidth / containerRect.width;

            const atomicBlocks = getAtomicBlockElements(element)
                .map((el) => toMmRange(el, containerRect.top, mmPerCssPx))
                .filter((b) => b.bottom > b.top);
            const rowBlocks = Array.from(element.querySelectorAll('tr'))
                .map((el) => toMmRange(el, containerRect.top, mmPerCssPx))
                .filter((b) => b.bottom > b.top);

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.85);
            const imgWidth = pageWidth;
            // Derived from the same live-DOM measurement as the block boundaries above
            // (not from canvas.width/height) so the two stay in one consistent coordinate
            // space — otherwise sub-pixel rounding drift between the DOM and the captured
            // canvas can shift a "safe" cut point into the middle of a block.
            const imgHeight = containerRect.height * mmPerCssPx;

            const pageBreaks = computePageBreaks(imgHeight, pageHeight, atomicBlocks, rowBlocks);

            // Each page is sized to exactly its own content height rather than a uniform
            // A4 height — jsPDF always clips to the page's *physical* size, so a page left
            // at full A4 height would still render whatever falls in the leftover gap below
            // a break, duplicating it when that same content is drawn again on the next page.
            let pdf = null;
            let cursor = 0;
            pageBreaks.forEach((end, idx) => {
                const pageContentHeight = end - cursor;
                // jsPDF normalizes 'p' to mean height >= width and silently swaps the two
                // otherwise, so a short trailing page (content shorter than it is wide) needs
                // to be declared 'l' or it comes out rotated.
                const orientation = pageContentHeight >= pageWidth ? 'p' : 'l';
                if (idx === 0) {
                    pdf = new jsPDF({ unit: 'mm', format: [pageWidth, pageContentHeight], orientation });
                } else {
                    pdf.addPage([pageWidth, pageContentHeight], orientation);
                }
                pdf.addImage(imgData, 'JPEG', 0, -cursor, imgWidth, imgHeight, undefined, 'FAST');
                cursor = end;
            });

            pdf.save(`${filename}.pdf`);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <button
            type="button"
            className="download-pdf-btn"
            onClick={handleDownloadPdf}
            disabled={isDownloading}
        >
            {isDownloading ? 'Generating...' : 'Download PDF'}
        </button>
    );
};

export default DownloadPdfButton;
